/**
 * EPBCS — Planning & Budgeting Cloud Services.
 * Executive layout: compact header + full-width dense content.
 * Navigation lives in the left sidebar: /planning/:section/:sub
 */
import React from "react";
import { useParams } from "react-router-dom";
import SetupSection from "./SetupSection";
import { RevenueSection, OpexSection } from "./Phase2Sections";
import CapexSection from "./CapexSection";
import WorkforceSection from "./WorkforceSection";
import FinancialsSection from "./FinancialsSection";
import DashboardsSection from "./DashboardsSection";
import AiAssistant from "./AiAssistant";

const SECTIONS = ["setup", "revenue", "opex", "capex", "workforce", "financials", "dashboards"];

const TITLES = {
  setup: "Setup & Masters", revenue: "Revenue Budgeting", opex: "OpEx Budgeting",
  capex: "CapEx & Depreciation", workforce: "Workforce Budgeting",
  financials: "Budgeted Financials", dashboards: "Executive Dashboards",
};

const SUB_TITLES = {
  setup: { masters: "Dimension Masters", cycle: "Budget Cycle", matrix: "Role Matrix", users: "User Access", import: "Data Import", drivers: "Driver Setup" },
  revenue: { other: "Other Revenue", driver: "Driver Based", contract: "Contract Based",
             product: "Product Based", hosp1: "Hospitality (CY Forecast)", hosp2: "Hospitality (Budget Year)" },
  opex: { contract: "Contract Based", driver: "Driver Based", other: "Other Expenses",
          hosp1: "Hospitality (CY Forecast)", hosp2: "Hospitality (Budget Year)",
          material: "Material Cost", bom: "Material BOM Master", transport: "Transportation Setup",
          fuel: "Fuel Cost", fleet: "Transportation Fleet" },
  capex: { wbs_new: "New Projects", wbs_existing: "Existing Projects", planning: "Capital Planning",
           assets_new: "New Asset Procurement", assets_existing: "Existing Assets",
           combos: "Valid Combinations", review: "Review" },
  workforce: { increment: "Increment Assumptions", attrition: "Attrition / Notice Period",
               existing: "Existing Employees", hires: "New Hires", scales: "Pay Scales", review: "Review" },
  financials: { income: "Income Statement", balance: "Balance Sheet", schedule3: "Schedule III Reports (Actuals)", approvals: "Approval Workflow", workflow: "Workflow Setup", sla: "SLA & Notifications" },
};

const SUBTITLES = {
  setup: "Planning dimensions, budget cycle, role matrix and user access — the foundation for every budgeting form.",
  revenue: "Manual input, increment %, driver-based, contract-based and trend-based revenue planning.",
  opex: "Operating expenses via contracts, drivers, increments and trends.",
  capex: "Project (WBS) and non-project capital expenditure with automated depreciation forecasting.",
  workforce: "Existing employee costs, new hire requests and grade pay scales.",
  financials: "Income Statement, Balance Sheet and Schedule III statutory reports from actuals.",
  dashboards: "Real-time monitoring of budgets, forecasts and headcount.",
};

export default function EpbcsPage() {
  const { section, sub } = useParams();
  const active = SECTIONS.includes(section) ? section : "setup";
  const subLabel = SUB_TITLES[active]?.[sub] || Object.values(SUB_TITLES[active] || {})[0];
  const title = active === "financials" && sub === "schedule3" ? "Financial Statements" : TITLES[active];

  return (
    <div className="flex flex-col min-h-full bg-[#F8F9FA]">
      <div className="flex-none bg-white border-b border-slate-200">
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold">
              <span className="text-[#C8A24B]">Planning &amp; Budgeting</span>
              {subLabel && <><span className="text-slate-300">/</span><span className="text-slate-500">{subLabel}</span></>}
            </div>
            <h1 className="mt-0.5 text-2xl font-semibold tracking-tight text-[#0F172A]" data-testid="epbcs-title">
              {title}
            </h1>
          </div>
          <p className="hidden xl:block max-w-md text-right text-xs leading-relaxed text-slate-500">
            {SUBTITLES[active]}
          </p>
        </div>
      </div>
      <div className="flex-1 p-4 md:p-6 w-full">
        {active === "setup" && <SetupSection sub={sub} />}
        {active === "revenue" && <RevenueSection sub={sub} />}
        {active === "opex" && <OpexSection sub={sub} />}
        {active === "capex" && <CapexSection sub={sub} />}
        {active === "workforce" && <WorkforceSection sub={sub} />}
        {active === "financials" && <FinancialsSection sub={sub} />}
        {active === "dashboards" && <DashboardsSection />}
      </div>
      <AiAssistant />
    </div>
  );
}
